import React, { useEffect, useState } from 'react';
import { Container, Form } from 'react-bootstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { Helmet } from 'react-helmet';
import { Loader, SectionHeader } from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  DefaultRateDetails,
  getDefaultRateDetailsAsync,
  selectDefaultRateDetails,
  selectDefaultRateDetailsLoadingStatus,
} from './defaultRatesSlice';
import AddOrUpdateDefaultRate from './AddOrUpdateDefaultRate';
import { LOADING_STATUS, PERMISSIONS } from '../../common/constants';
import { checkPermission } from '../auth/authSlice';

function DefaultRate({
  writePermissions,
}: {
  writePermissions?: Array<PERMISSIONS>;
}) {
  const location = useLocation();
  const params = useParams();
  const parsedQuery = queryString.parse(location.search.replace('?', ''));
  const dispatch = useAppDispatch();
  const defaultRateDetails = useAppSelector(selectDefaultRateDetails);
  const defaultRateDetailsLoadingStatus = useAppSelector(
    selectDefaultRateDetailsLoadingStatus,
  );
  const [selectedAccommodationId, setSelectedAccommodationId] = useState(null);
  const [selectedAccommodationName, setSelectedAccommodationName] =
    useState<string>('');
  const [isAddMode, setIsAddMode] = useState(!params.accommodationId);

  const hasWriteAccess = useAppSelector(checkPermission(writePermissions));

  const initialDefaultRateState: DefaultRateDetails = {
    accommodationId: '',
    priceForAdditionalDay: 0,
    dailyPurchasePrice: 0,
    commissionPercent: 0,
    checkInTime: '',
    checkOutTime: '',
    ratePrices: [
      { duration: 1, dailyPrice: 0, dailyPurchasePrice: 0 },
      { duration: 2, dailyPrice: 0, dailyPurchasePrice: 0 },
      { duration: 3, dailyPrice: 0, dailyPurchasePrice: 0 },
      { duration: 4, dailyPrice: 0, dailyPurchasePrice: 0 },
      { duration: 5, dailyPrice: 0, dailyPurchasePrice: 0 },
      { duration: 6, dailyPrice: 0, dailyPurchasePrice: 0 },
      { duration: 7, dailyPrice: 0, dailyPurchasePrice: 0 },
    ],
  };

  const [defaultRate, setDefaultRate] = useState(initialDefaultRateState);

  const getDefaultRateAndAccommodationId = (accommodationId: string) => {
    const newDefaultRate = { ...defaultRate };
    newDefaultRate.accommodationId = accommodationId;
    setDefaultRate(newDefaultRate);
    dispatch(getDefaultRateDetailsAsync(accommodationId));
    setSelectedAccommodationId(accommodationId);
  };

  useEffect(() => {
    getDefaultRateAndAccommodationId(params.accommodationId);

    if (parsedQuery.accommodation && parsedQuery.accommodation !== '') {
      setSelectedAccommodationName(
        decodeURI(parsedQuery.accommodation.toString()),
      );
    }
  }, [params]);

  useEffect(() => {
    if (!defaultRateDetails) {
      setIsAddMode(true);
      const initial = initialDefaultRateState;
      initial.accommodationId = selectedAccommodationId;
      setDefaultRate(initial);
    } else {
      setDefaultRate(defaultRateDetails);
      setIsAddMode(false);
    }
  }, [defaultRateDetails]);

  return (
    <>
      <Helmet>
        <title>
          Hinnoittelu - Oletushintakauden muokkaus - {selectedAccommodationName}
        </title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink="/rates/defaultrates"
          Breadcrumbs={['Hinnoittelu', 'Oletushintakaudet']}
          Title={`Muokkaa oletushintakautta: ${selectedAccommodationName}`}
        />
        <div className="mb-24">
          <Link
            to={`/rates/seasonrates/${defaultRate.accommodationId}?accommodation=${selectedAccommodationName}`}
          >
            Siirry kohteen hintakausiin
          </Link>
          <br />
          <Link to={`/accommodations/details/${defaultRate.accommodationId}`}>
            Siirry kohteen tietoihin
          </Link>
        </div>

        {defaultRate && defaultRate.accommodationId && (
          <Form className="mt-48">
            <Loader
              IsLoading={
                defaultRateDetailsLoadingStatus === LOADING_STATUS.LOADING
              }
            />
            {defaultRateDetailsLoadingStatus === LOADING_STATUS.IDLE && (
              <AddOrUpdateDefaultRate
                defaultRateItem={defaultRate}
                isAddMode={isAddMode}
                hasWriteAccess={hasWriteAccess}
              />
            )}
          </Form>
        )}
      </Container>
    </>
  );
}

export default DefaultRate;
